import { useEffect, useState } from 'react';
import TankModal, { TankFormValues } from '../../../components/location-management/TankModal';
import OnFarmBillingAgreementModal from '../../../components/on-farm-billing-agreement/OnFarmBillingAgreementModal';
import { AccountStorageSite, AccountStorageTank } from '../../../types/interfaces/accountStorage.interfaces';
import { createAccountStorageTank, updateAccountStorageTank } from '../../../api/accountStorageItemsApi';
import { acceptOnFarmBillingAgreement, getOnFarmBillingAgreementStatus } from '../../../api/onFarmBillingAgreementApi';
import { OnFarmBillingAgreementStatus } from '../../../types/interfaces/onFarmBillingAgreement.interfaces';
import { showToast } from '../../../services/toast.service';
import { toastMessages } from '../../../constants/errorMessages';
import { ON_FARM_BILLING_CONSTANTS } from '../../../constants/common';

type OnFarmTankModalProps = {
  mode: 'add' | 'edit';
  close: () => void;
  site: AccountStorageSite;
  tank?: AccountStorageTank;
  setSelectedId?: (id: number) => void;
};

const deferredForAgreement = { suppressToast: true };

const OnFarmTankModal = ({ mode, close, site, tank, setSelectedId }: OnFarmTankModalProps): JSX.Element => {
  const [agreementStatus, setAgreementStatus] = useState<OnFarmBillingAgreementStatus | null>(null);
  const [agreementLoading, setAgreementLoading] = useState(true);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [pendingValues, setPendingValues] = useState<TankFormValues | null>(null);
  const [agreementSubmitting, setAgreementSubmitting] = useState(false);

  const tankValues = tank
    ? {
        name: tank.name,
        active: tank.active,
        isTravelingTank: tank.isTravelingTank,
        isMaintenanceEnabled: tank.isMaintenanceEnabled ?? true,
      }
    : undefined;

  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      setAgreementLoading(true);
      try {
        const { data } = await getOnFarmBillingAgreementStatus();
        if (!cancelled) {
          setAgreementStatus(data);
        }
      } catch (error: any) {
        if (!cancelled) {
          setAgreementStatus(null);
          showToast.error(error?.response?.data?.title || toastMessages.SOMETHING_WENT_WRONG);
        }
      } finally {
        if (!cancelled) {
          setAgreementLoading(false);
        }
      }
    };
    loadStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const persistTank = async (values: TankFormValues): Promise<number> => {
    if (mode === 'add') {
      const { data: id } = await createAccountStorageTank({
        name: values.name,
        active: values.active,
        isTravelingTank: values.isTravelingTank,
        isMaintenanceEnabled: values.isMaintenanceEnabled ?? true,
        accountStorageSiteId: site.accountStorageSiteId,
      });
      return id;
    }

    if (!tank) {
      throw new Error(toastMessages.SOMETHING_WENT_WRONG);
    }

    await updateAccountStorageTank(tank.accountStorageTankId, {
      name: values.name,
      active: values.active,
      isTravelingTank: values.isTravelingTank,
      isMaintenanceEnabled: values.isMaintenanceEnabled ?? true,
    });
    return tank.accountStorageTankId;
  };

  const maybeRequireAgreement = (values: TankFormValues) => {
    if (agreementLoading) {
      throw new Error(toastMessages.SOMETHING_WENT_WRONG);
    }

    if (values.active && agreementStatus?.needsAcceptance) {
      setPendingValues(values);
      setShowAgreementModal(true);
      throw deferredForAgreement;
    }

    if (values.active && !agreementStatus) {
      showToast.error(ON_FARM_BILLING_CONSTANTS.AGREEMENT_REQUIRED);
      throw deferredForAgreement;
    }
  };

  const createTank = async (values: TankFormValues) => {
    maybeRequireAgreement(values);
    return persistTank(values);
  };

  const updateTank = async (values: TankFormValues) => {
    maybeRequireAgreement(values);
    await persistTank(values);
  };

  const handleAgreementAccept = async () => {
    if (!pendingValues || !agreementStatus) {
      return;
    }

    setAgreementSubmitting(true);
    try {
      await acceptOnFarmBillingAgreement({
        termsVersion: agreementStatus.currentTermsVersion,
      });
      setAgreementStatus(prev => (prev ? { ...prev, needsAcceptance: false } : prev));
      const id = await persistTank(pendingValues);
      if (mode === 'add') {
        setSelectedId?.(id);
        showToast.success(toastMessages.TANK_CREATED_SUCCESS);
      } else {
        showToast.success(toastMessages.TANK_UPDATE_SUCCESS);
      }
      setShowAgreementModal(false);
      setPendingValues(null);
      close();
    } catch (error: any) {
      showToast.error(error?.response?.data?.title || toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setAgreementSubmitting(false);
    }
  };

  return (
    <>
      <TankModal
        mode={mode}
        close={close}
        siteName={site.name}
        tank={pendingValues ?? tankValues}
        code={tank?.tankCode}
        showMaintenanceToggle
        setSelectedId={setSelectedId}
        createTank={createTank}
        updateTank={updateTank}
      />

      {agreementStatus && (
        <OnFarmBillingAgreementModal
          isOpen={showAgreementModal}
          status={agreementStatus}
          submitting={agreementSubmitting}
          onClose={() => {
            setShowAgreementModal(false);
            setPendingValues(null);
          }}
          onAccept={handleAgreementAccept}
        />
      )}
    </>
  );
};

export default OnFarmTankModal;
